import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Container,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { AvForm } from "availity-reactstrap-validation";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { getRecords, saveRecords } from "../../../services/api.service";
import SelectInput from "../../../custom/components/Select-Input/SelectInput";
import CheckboxInput from "../../../custom/components/CheckBox-Input/CheckboxInput";
import ProjectAccessModal from "../../../components/CommonForBoth/TopbarDropdown/ProjectAccess.js/ProjectAccessModel";

const ProjectAccess = () => {
  const userType = [
    { label: "Bermad User", value: 1 },
    { label: "Subcontractor", value: 2 },
    { label: "Customer", value: 3 },
  ];

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectlistOptions, setProjectlistOptions] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [userlistOptions] = useState(userType);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ProjectError, setProjectError] = useState(false);
  const [UserTypeError, setUserTypeError] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedData, setSelectedData] = useState([]);

  useEffect(() => {
    getProjectData();
  }, []);

  const getProjectData = async () => {
    try {
      const payload = {
        modelName: "projects",
        whereCondition: { is_active: true },
      };
      const responseData = await getRecords(payload, "/getRecords");
      if (!responseData) {
        throw new Error("No response received from server");
      }

      if (responseData?.data && Array.isArray(responseData?.data)) {
        const projectOptions =
          responseData &&
          responseData?.data &&
          responseData?.data?.map((project) => ({
            label: project?.project_name,
            value: project?.id,
          }));
        setProjectlistOptions(projectOptions);
      } else {
        throw new Error("Invalid data format received");
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
      toast.error(
        error.message || "Failed to load projects. Please try again later."
      );
    }
  };

  const handleProjectSelectChange = (project) => {
    setSelectedProject(project);
    setProjectError(false);
    setSelectedUserType(null);
    setData([]);
    setSelectedData([]);
  };

  const handleUserSelectChange = (userType) => {
    setSelectedUserType(userType);
    setUserTypeError(false);
    setData([]);
    setSelectedData([]);
  };

  const handleGo = async () => {
    try {
      if (!selectedProject || !selectedUserType) {
        setProjectError(!selectedProject);
        setUserTypeError(!selectedUserType);
        return;
      }

      const payload = {
        modelName: "user_masters",
        whereCondition: {
          is_active: true,
          user_type: selectedUserType?.value,
        },
        relations: [
          {
            module: "project_access",
            moduleAs: "project_access",
            subModule: "notification_accesses",
            subModuleAs: "notification_accesses",
          },
        ],
      };

      setLoading(true);
      const responseData = await getRecords(payload, "/getRecords");

      if (!responseData?.data?.length > 0) {
        throw new Error(responseData?.message || "Failed to fetch users");
      }

      const users = responseData?.data || [];

      if (users?.length === 0) {
        setData([]);
        toast.info("No users found for the selected user type.");
        return;
      }

      const usersWithAccess = users?.map((user) => {
        const hasAccess =
          user.project_access &&
          user.project_access?.some(
            (access) =>
              access?.project_id === selectedProject?.value && access?.access
          );

        return { ...user, isChecked: hasAccess };
      });

      setData(usersWithAccess);
    } catch (error) {
      console.error("Error in handleGo:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (userId, isChecked) => {
    setData((prevData) => {
      const updatedData = prevData.map((user) =>
        user.id === userId ? { ...user, isChecked } : user
      );
      return updatedData;
    });
  };

  const handleSelectItem = (item) => {
    setSelectedId(item.id);

    const projectAccess = item?.project_access?.find(
      (access) => access?.project_id === selectedProject?.value
    );

    if (!projectAccess || !projectAccess?.is_active || !item?.isChecked) {
      setIsProjectModalOpen(false);
      toast.error("User does not have access to this project.");
      return;
    }

    const defaultNotificationData = [
      { id: 1, name: "Order", isChecked: false, key: "order" },
      { id: 2, name: "Invoice", isChecked: false, key: "invoice" },
      { id: 3, name: "Inward", isChecked: false, key: "inward" },
      { id: 4, name: "Outward", isChecked: false, key: "outward" },
      { id: 5, name: "Installation", isChecked: false, key: "installation" },
      {
        id: 6,
        name: "Installation Approval",
        isChecked: false,
        key: "installation_approval",
      },
      {
        id: 7,
        name: "Installation Claim",
        isChecked: false,
        key: "installation_claim",
      },
      { id: 8, name: "Commissioning", isChecked: false, key: "commissioning" },
      {
        id: 9,
        name: "Commissioning Claim",
        isChecked: false,
        key: "commissioning_claim",
      },
      {
        id: 10,
        name: "Shortage",
        isChecked: false,
        key: "shortage",
      },
    ];

    // Check if the user already has saved data in selectedData
    const existingUserData =
      selectedData && selectedData?.find((user) => user.user_id === item.id);

    // If user data exists, use it; otherwise, initialize from projectAccess
    const userNotificationData = defaultNotificationData?.map((item) => {
      if (existingUserData) {
        const existingNotification =
          existingUserData?.notification_accesses?.find(
            (n) => n.key === item.key
          );
        return {
          ...item,
          isChecked: existingNotification
            ? existingNotification?.isChecked
            : false,
        };
      } else {
        const notificationAccess = projectAccess?.notification_accesses?.find(
          (access) => access.notification_type === item.key
        );
        return {
          ...item,
          isChecked: notificationAccess ? notificationAccess.access : false,
        };
      }
    });

    setIsProjectModalOpen(true);
    setSelectedData((prevData) => {
      const existingUserIndex = prevData.findIndex(
        (user) => user.user_id === item.id
      );

      if (existingUserIndex !== -1) {
        // Update existing user data
        const updatedData = [...prevData];
        updatedData[existingUserIndex] = {
          user_id: item.id,
          access: item.isChecked,
          notification_accesses: userNotificationData,
        };
        return updatedData;
      } else {
        // Add new user data
        return [
          ...prevData,
          {
            user_id: item.id,
            access: item.isChecked,
            notification_accesses: userNotificationData,
          },
        ];
      }
    });
  };

  const handleClose = () => {
    setSelectedId(null);
    setIsProjectModalOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedProject || !selectedUserType) {
      setProjectError(!selectedProject ? true : false);
      setUserTypeError(!selectedUserType ? true : false);
      return;
    }

    const checkedUsers = data && data?.filter((user) => user.isChecked);

    if (checkedUsers?.length === 0) {
      toast.error("Please select at least one user.");
      return;
    }
    setLoading(true);

    let payload = {
      modelName: "project_access",
      inputData:
        data &&
        data?.map((user) => {
          const existingAccess = user?.project_access?.find(
            (access) => access?.project_id === selectedProject?.value
          );

          const userNotifications =
            selectedData && selectedData?.find((u) => u.user_id === user.id);
          return {
            id: existingAccess ? existingAccess?.id : null,
            project_id: selectedProject?.value,
            user_type: selectedUserType?.value,
            user_id: user?.id,
            access: user?.isChecked,
            is_active: true,
            notification_accesses:
              userNotifications?.notification_accesses?.map((n) => ({
                notification_type: n?.key,
                access: n?.isChecked,
              })) || [],
          };
        }),
    };

    try {
      const response = await saveRecords(
        payload,
        "/createOrUpdateProjectAccess"
      );

      if (response?.code === 1) {
        let projectDetailsData =
          JSON.parse(localStorage.getItem("projectDetails")) || [];

        const accessProjectPayload = {
          modelName: "project_access",
          whereCondition: {
            user_id: projectDetailsData[0]?.user_id,
          },
          relations: [
            {
              module: "projects",
              moduleAs: "projectDetails",
              required: true,
            },
          ],
        };

        const isAccessProjectResponseData = await getRecords(
          accessProjectPayload,
          "/UserWiseProjectListing"
        );

        isAccessProjectResponseData?.data?.forEach((input) => {
          let matchingProject = projectDetailsData.find(
            (project) =>
              project.id === input.id &&
              project.project_id === input.project_id &&
              project.user_type === input.user_type &&
              project.user_id === input.user_id
          );

          if (matchingProject) {
            matchingProject.access = input?.access;
          }
        });

        projectDetailsData?.forEach((localProject) => {
          let isMatched = isAccessProjectResponseData?.data?.some(
            (responseProject) =>
              responseProject.project_id === localProject.project_id &&
              responseProject.user_id === localProject.user_id &&
              responseProject.user_type === localProject.user_type
          );

          if (!isMatched) {
            isAccessProjectResponseData?.data?.push(localProject);
          }
        });

        localStorage.setItem(
          "projectDetails",
          JSON.stringify(isAccessProjectResponseData?.data)
        );

        setData([]);
        setSelectedProject(null);
        setSelectedUserType(null);
        setLoading(false);
        toast.success("Access successfully updated.");
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        toast.error("Failed to update access.");
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An error occurred while submitting data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Project ACCESS" breadcrumbItems={[]} />
          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  <AvForm className="justify-content-center">
                    <Row>
                      <Col md={4}>
                        <SelectInput
                          label="Project"
                          value={selectedProject}
                          onSelect={handleProjectSelectChange}
                          options={projectlistOptions}
                          classNamePrefix="select2-selection"
                          noOptionsMessage={() => "No data found."}
                          maxMenuHeight={200}
                          asterisk={true}
                          isClearable={true}
                          customError={
                            (ProjectError && "Please select a project.") || ""
                          }
                        />
                      </Col>
                      <Col md={4}>
                        <SelectInput
                          label="User Type"
                          value={selectedUserType}
                          onSelect={handleUserSelectChange}
                          options={userlistOptions}
                          classNamePrefix="select2-selection"
                          noOptionsMessage={() => "No data found."}
                          maxMenuHeight={200}
                          asterisk={true}
                          isClearable={true}
                          customError={
                            (UserTypeError && "Please select a user type.") ||
                            ""
                          }
                        />
                      </Col>

                      <Col
                        md={1}
                        style={{ marginTop: "28px", paddingBottom: "20px" }}
                      >
                        <Button
                          onClick={handleGo}
                          color="primary"
                          type="button"
                          className="waves-effect waves-light me-1"
                          style={{ background: "#006eb9" }}
                        >
                          Go
                        </Button>
                      </Col>
                    </Row>

                    <Row
                      className="justify-content-center table mt-2"
                      style={{
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "55vh",
                        width: "100%",
                        scrollBehavior: "smooth",
                        scrollbarWidth: "thin",
                        msOverflowStyle: "none",
                        zIndex: 0,
                      }}
                    >
                      {loading ? (
                        <div style={{ textAlign: "center", marginTop: "20px" }}>
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <Row style={{ width: "100%" }}>
                          <Col xl="12">
                            <table className="table table-bordered">
                              <thead
                                style={{
                                  backgroundColor: "#8AC1E8",
                                  position: "sticky",
                                  top: 0,
                                  backgroundColor: "#8AC1E8",
                                  zIndex: 1,
                                }}
                              >
                                <tr>
                                  <th>User Name</th>
                                  <th>Action</th>
                                  <th>Notification Alert</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data?.length > 0 ? (
                                  data?.map((item) => (
                                    <tr key={item?.id}>
                                      <td>{`${item?.first_name} ${item?.last_name}`}</td>
                                      <td>
                                        <CheckboxInput
                                          value={item?.isChecked}
                                          checked={item?.isChecked}
                                          handleTextChange={(e) =>
                                            handleCheckboxChange(
                                              item?.id,
                                              e.target.checked
                                            )
                                          }
                                          parentClassName="form-check"
                                          checkBoxClassName="form-check-input"
                                          labelClassName="form-check-label"
                                          labelStyle={{ display: "none" }}
                                          checkboxStyle={{
                                            width: "16px",
                                            height: "16px",
                                          }}
                                        />
                                      </td>
                                      <td>
                                        {
                                          <Link
                                            className="me-3 text-primary"
                                            onClick={() =>
                                              handleSelectItem(item)
                                            }
                                          >
                                            {item?.isChecked == true ? (
                                              <i className="fa fa-bell font-size-18"></i>
                                            ) : (
                                              <i className="fa fa-bell-slash font-size-18"></i>
                                            )}
                                          </Link>
                                        }
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan="10"
                                      style={{ textAlign: "center" }}
                                    >
                                      No users found.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </Col>
                        </Row>
                      )}
                    </Row>

                    {data?.length > 0 && (
                      <Row className="mt-4 justify-content-center ">
                        <Col
                          style={{
                            paddingTop: "10px",
                            borderTop: "1px solid #ced4da",
                            display: "flex",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <Button color="primary" onClick={handleSubmit}>
                            Submit
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </AvForm>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {isProjectModalOpen && (
        <ProjectAccessModal
          onClose={handleClose}
          data={selectedData}
          setData={setSelectedData}
          user_id={selectedId}
        />
      )}
    </React.Fragment>
  );
};

export default ProjectAccess;
